import { useEffect, useRef, useState } from "react";
import { serverFetch } from "../../../server";
import { Button, Checkbox, Input, message, Table } from "antd";
import { hasSubstring } from "../../../extensions/helper";
import CatalogGroup from "./catalog-group";
import CatalogItem from "./catalog-item";
import "./catalogs.css";

const Catalogs = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editGroupId, setEditGroupId] = useState();
  const [editItemId, setEditItemId] = useState();
  const [selectedGroupId, setSelectedGroupId] = useState();
  const [filter, setFilter] = useState({ showHidden: false, searchText: "" });
  const [draggedItemId, setDraggedItemId] = useState(null);
  const [dragOverGroupId, setDragOverGroupId] = useState(null);
  const divRef = useRef(null);

  const getData = () => {
    setLoading(true);
    serverFetch("Catalog/Groups")
      .then(data => {
        setGroups(data);
        setLoading(false);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  useEffect(getData, []);

  const onCloseGroup = (refresh) => {
    setEditGroupId();
    if (refresh) {
      getData();
    }
  };

  const onCloseItem = (refresh) => {
    setEditItemId();
    refresh && getData();
  };

  const handleItemDragStart = (e, item) => {
    setDraggedItemId(item.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({
      itemId: item.id,
      currentGroupId: item.catalogGroupId
    }));
  };

  const handleItemDragEnd = () => {
    setDraggedItemId(null);
    setDragOverGroupId(null);
  };

  const handleGroupDragOver = (e, groupId) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverGroupId(groupId);
  };

  const handleGroupDragLeave = () => {
    setDragOverGroupId(null);
  };

  const handleGroupDrop = (e, targetGroupId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverGroupId(null);
    
    try {
      const dragData = JSON.parse(e.dataTransfer.getData('text/plain'));
      const itemId = dragData.itemId;
      const currentGroupId = dragData.currentGroupId;

      // Если элемент уже в этой группе, ничего не делаем
      if (currentGroupId === targetGroupId) {
        return;
      }

      // Находим элемент в текущих данных
      const sourceGroup = groups.find(g => g.id === currentGroupId);
      const item = sourceGroup?.items?.find(i => i.id === itemId);
      
      if (!item) {
        message.error("Элемент не найден");
        return;
      }

      // Обновляем элемент на сервере
      setLoading(true);
      const updatedItem = { ...item, catalogGroupId: targetGroupId };
      serverFetch("Catalog/Items", { method: "PUT", bodyData: updatedItem })
        .then(() => {
          setLoading(false);
          message.success("Элемент перемещен");
          getData();
        })
        .catch(e => {
          setLoading(false);
          message.error(e.userMessage);
        });
    } catch (error) {
      console.error('Error handling drop:', error);
      setDragOverGroupId(null);
    }
  };


  const columns = [
    { title: "#", dataIndex: "id", width: 100 },
    { title: "Пор", dataIndex: "idx", width: 48 },
    { title: "Акт", dataIndex: "active", width: 48, align: "center", render: value => <Checkbox checked={value} disabled /> },
    {
      dataIndex: "imageId",
      width: 48,
      align: "center",
      render: (imageId, record) => (
        <img
          src={imageId ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${imageId}.jpg` : "/images/room.svg"}
          alt={record.name}
          style={{ maxWidth: "100%", maxHeight: 32, objectFit: "contain", display: "block", margin: "0 auto" }}
        />
      )
    },
    { title: "Наименование", dataIndex: "name", width: 400 },
  ];

  const headerSize = divRef.current?.querySelector('.ant-table-thead')?.offsetHeight ?? 0;
  const tableHeight = divRef.current ? (document.documentElement.offsetHeight - headerSize - divRef.current.getBoundingClientRect().top - window.scrollY) : 0;
  const tableWidth = (document.documentElement.offsetWidth - divRef.current?.getBoundingClientRect().left) || 0;

  const onChangeFilter = value => setFilter({ ...filter, ...value });

  const filtered = groups.filter(group => {
    if (!filter.showHidden && !group.active) return false;
    if (!hasSubstring(filter.searchText, group.name)) return false;
    return true;
  });

  const divGroup = editGroupId != null && <CatalogGroup id={editGroupId} onClose={onCloseGroup} onDelete={getData} />;
  const divItem = editItemId != null && <CatalogItem id={editItemId} groupId={selectedGroupId} onClose={onCloseItem} />;

  return (
    <div>
      <div className="sport-form-title">
        <Checkbox checked={filter.showHidden} onChange={e => onChangeFilter({ showHidden: e.target.checked })}>
          Показывать неактивные
        </Checkbox>
        <Input
          placeholder="Поиск"
          value={filter.searchText}
          onChange={e => onChangeFilter({ searchText: e.target.value })}
        />
        <Button onClick={getData}>Обновить</Button>
        <Button onClick={() => setEditGroupId(0)}>Новая группа</Button>
      </div>
      <div ref={divRef} style={{ width: tableWidth }}>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={false}
          scroll={{ y: tableHeight }}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: [selectedGroupId],
            renderCell: checked => <div className={checked ? "list-indicator" : ""} />
          }}
          onRow={(record) => ({
            onClick: () => {
              setSelectedGroupId(record.id);
              setEditGroupId(record.id);
            },
            onDragOver: (e) => handleGroupDragOver(e, record.id),
            onDragLeave: handleGroupDragLeave,
            onDrop: (e) => handleGroupDrop(e, record.id),
            className: dragOverGroupId === record.id ? 'drag-over' : ''
          })}
          expandable={{
            expandedRowRender: (record) => {
              const items = record.items || [];
              return (
                <div style={{ margin: "8px 0", paddingLeft: "24px" }}>
                  <div style={{ marginBottom: "8px", display: "flex", gap: "8px", alignItems: "center" }}>
                    <Button size="small" onClick={() => {
                      setSelectedGroupId(record.id);
                      setEditItemId(0);
                    }}>Добавить</Button>
                  </div>
                  {items.length === 0 ? (
                    <div style={{ color: "#999", fontStyle: "italic" }}>Нет элементов</div>
                  ) : (
                    <Table
                      dataSource={items}
                      columns={[
                        { title: "#", dataIndex: "id", width: 80 },
                        { title: "Наименование", dataIndex: "name", width: 300 },
                        { title: "Описание", dataIndex: "description" },
                        {
                          title: "Цена",
                          dataIndex: "price",
                          width: 120,
                          align: "right",
                          render: (price) => price ? `${price.toFixed(2)}` : "-"
                        },
                        {
                          title: "Акт",
                          dataIndex: "active",
                          width: 48,
                          align: "center",
                          render: value => <Checkbox checked={value} disabled />
                        }
                      ]}
                      rowKey="id"
                      size="small"
                      pagination={false}
                      onRow={(itemRecord) => ({
                        onClick: () => {
                          setSelectedGroupId(record.id);
                          setEditItemId(itemRecord.id);
                        },
                        draggable: true,
                        onDragStart: (e) => handleItemDragStart(e, itemRecord),
                        onDragEnd: handleItemDragEnd,
                        className: draggedItemId === itemRecord.id ? 'dragging' : ''
                      })}
                    />
                  )}
                </div>
              );
            },
            rowExpandable: () => true
          }}
        />
      </div>
      {divGroup}
      {divItem}
    </div>
  );
};

export default Catalogs;

